package com.iptvplayer.shared.data.repository

import com.iptvplayer.shared.data.local.preferences.TokenStorage
import com.iptvplayer.shared.data.remote.api.DeviceApi
import com.iptvplayer.shared.data.remote.dto.RegisterDeviceRequestDto
import com.iptvplayer.shared.domain.model.Device
import com.iptvplayer.shared.domain.model.DevicePlatform
import com.iptvplayer.shared.domain.repository.DeviceRepository
import com.iptvplayer.shared.util.Result

class DeviceRepositoryImpl(
    private val deviceApi: DeviceApi,
    private val tokenStorage: TokenStorage
) : DeviceRepository {

    override suspend fun getDevices(): Result<List<Device>> = try {
        val devices = deviceApi.getDevices().map { dto ->
            Device(
                id = dto.id,
                userId = dto.userId,
                name = dto.name,
                platform = parsePlatform(dto.platform),
                isOnline = dto.isOnline,
                lastSeenAt = dto.lastSeenAt,
                registeredAt = dto.registeredAt
            )
        }
        Result.Success(devices)
    } catch (e: Exception) {
        Result.Error("Failed to get devices: ${e.message}", e)
    }

    override suspend fun registerDevice(name: String, platform: DevicePlatform): Result<Device> = try {
        val dto = deviceApi.registerDevice(
            RegisterDeviceRequestDto(
                name = name,
                platform = platform.name
            )
        )
        val device = Device(
            id = dto.id,
            userId = dto.userId,
            name = dto.name,
            platform = parsePlatform(dto.platform),
            isOnline = dto.isOnline,
            lastSeenAt = dto.lastSeenAt,
            registeredAt = dto.registeredAt
        )
        tokenStorage.saveDeviceId(device.id)
        Result.Success(device)
    } catch (e: Exception) {
        Result.Error("Failed to register device: ${e.message}", e)
    }

    override suspend fun renameDevice(deviceId: String, name: String): Result<Unit> = try {
        deviceApi.renameDevice(deviceId, name)
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to rename device: ${e.message}", e)
    }

    override suspend fun removeDevice(deviceId: String): Result<Unit> = try {
        deviceApi.removeDevice(deviceId)
        if (tokenStorage.getDeviceId() == deviceId) {
            tokenStorage.saveDeviceId("")
        }
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to remove device: ${e.message}", e)
    }

    override suspend fun sendHeartbeat(deviceId: String): Result<Unit> = try {
        deviceApi.sendHeartbeat(deviceId)
        Result.Success(Unit)
    } catch (e: Exception) {
        Result.Error("Failed to send heartbeat: ${e.message}", e)
    }

    override fun getLocalDeviceId(): String? {
        val id = tokenStorage.getDeviceId()
        return if (id.isNullOrEmpty()) null else id
    }

    override suspend fun saveLocalDeviceId(deviceId: String) {
        tokenStorage.saveDeviceId(deviceId)
    }

    private fun parsePlatform(platform: String): DevicePlatform = try {
        DevicePlatform.valueOf(platform)
    } catch (_: Exception) {
        DevicePlatform.ANDROID_MOBILE
    }
}
